import { create } from 'zustand';
import {
  PermissionModel,
  RoleCreateModel,
  RoleFilter,
  RoleInfoModel,
  RolePermissionModel,
  RoleUpdateModel,
} from '../model/role.model';
import axiosInstance from '../../../configs/axios';
import { RoleChildrenModel } from '../data/role.modal.child';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';
import { roleRefData } from '../data/role-data-ref';
import { ApiPath } from '../../../constants/apiPath';
import CusttomAlertController from '../../../alert/controller/custom.alert.controller';
import { CustomAlertType } from '../../../alert/data/custom.alert.type';
import { CustomAlertIcons } from '../../../alert/data/custom.alert.icon';
export interface RoleControllerState {
  listRole: RoleInfoModel[] | [];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: RoleFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: RoleChildrenModel | null;
  formCreate: RoleCreateModel | null;
  formUpdate: RoleUpdateModel | null;
  selectedRole: RoleInfoModel | null;
  allPermissions: PermissionModel[];
  permissionLoading: boolean;
  /**
   * permission_id -> role-permission uuid for `selectedRole`. Read from
   * `role-permissions/all` rather than the copy embedded in `roles/all`:
   * assigning a permission does not invalidate the roles cache on the API,
   * so that copy can be up to an hour stale.
   */
  assignedPermissions: Record<number, string>;
  /** Checkbox state of the open permission modal, saved as one batch. */
  draftPermissionIds: number[];
  savingPermissions: boolean;
}

export interface RoleControllerAction {
  roleGetData: (startLoading?: boolean, filter?: RoleFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: RoleFilter | null) => void;
  onHandleModal: (activeModal?: RoleChildrenModel | null) => void;
  onUpdateFormCreate: (form: RoleCreateModel | null) => void;
  onUpdateFormUpdate: (form: RoleUpdateModel | null) => void;
  onSubmitForm: (form: RoleCreateModel | null) => void;
  onSubmitFormUpdate: (form: RoleUpdateModel | null) => void;
  onDelete: (id: number) => void;
  setMessage: (str: string) => void;
  openPermissionsModal: (role: RoleInfoModel) => void;
  loadAllPermissions: () => Promise<void>;
  loadRolePermissions: (roleId: number) => Promise<void>;
  onToggleDraftPermission: (permissionId: number) => void;
  onToggleDraftGroup: (permissionIds: number[], checked: boolean) => void;
  onSaveRolePermissions: () => Promise<void>;
}
export const RoleController = create<RoleControllerState & RoleControllerAction>()((set, get) => ({
  loading: false,
  listRole: [],
  message: null,
  listCount: 0,
  page: 0,
  pageRowAmount: 9,
  filter: { skip: 0, take: 9 },
  activeModal: null,
  formCreate: null,
  formUpdate: null,
  selectedRole: null,
  allPermissions: [],
  permissionLoading: false,
  assignedPermissions: {},
  draftPermissionIds: [],
  savingPermissions: false,
  roleGetData: async (startLoading?: boolean, filter?: RoleFilter | null) => {
    try {
      if (startLoading) set(() => ({ loading: true }));
      const res = await axiosInstance.get(ApiPath.roles.all, {
        params: filter ?? { skip: 0, take: 9 },
      });
      const filteredData: RoleInfoModel[] = res?.data?.data ?? [];
      const filteredDataTotal: number = res?.data?.meta?.total ?? 0;
      set(() => ({ listRole: filteredData, listCount: filteredDataTotal }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
  onPageChange: (page: number, rows: number) => {
    set(() => ({ pageRowAmount: rows, page: page }));
  },
  onFilterChange: (filter?: RoleFilter | null) => {
    set(() => ({ filter: filter }));
    get().roleGetData(true, filter);
  },
  onHandleModal: (activeModal?: RoleChildrenModel | null) => {
    set(() => ({ activeModal: activeModal, message: null }));
    if (activeModal === null) {
      set(() => ({
        formCreate: null,
        selectedRole: null,
        assignedPermissions: {},
        draftPermissionIds: [],
      }));
    }
  },
  onUpdateFormCreate: (form: RoleCreateModel | null) => {
    set(() => ({ formCreate: form, message: null }));
  },
  onUpdateFormUpdate: (form: RoleUpdateModel | null) => {
    set(() => ({ formUpdate: form, message: null }));
  },
  onSubmitForm: async (form: RoleCreateModel | null) => {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }

    if (!form?.role_name || form?.role_name?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'Role name required!' }));
    } else if (!form?.level || form?.level === null) {
      set(() => ({ formCreate: { ...form }, message: 'Level required!' }));
    } else if (!form?.description || form?.description?.trim() === '') {
      set(() => ({ formCreate: { ...form }, message: 'Description required!' }));
    } else if (form.level > 5 || form.level < 1) {
      set(() => ({
        formCreate: { ...form },
        message: `ເລກ ເລເວລ ບໍ່ໄດ້ຢູ່ໃນຂອບເຂດທີ່ກຳນົດໄວ້! (${roleRefData.map((item) => item.level).join(', ')})`,
      }));
    } else {
      try {
        set(() => ({ formCreate: form, loading: true }));
        const res = await axiosInstance.post(ApiPath.roles.create, form);
        if (res?.status >= 200 && res?.status < 300) {
          // Back to the first page, but keep whatever is typed in the search box.
          const nextFilter = { ...get().filter, skip: 0, take: get().pageRowAmount };
          set(() => ({ message: null, formCreate: null, filter: nextFilter, page: 0 }));
          get().roleGetData(true, nextFilter);
          setTimeout(() => get().onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  onSubmitFormUpdate: async (form: RoleUpdateModel | null) => {
    if (form === null) {
      set(() => ({ message: 'Form is empty!' }));
      return;
    }

    if (!form?.role_name || form?.role_name?.trim() === '') {
      set(() => ({ formUpdate: { ...form }, message: 'Role name required!' }));
    } else if (!form?.level || form?.level === null) {
      set(() => ({ formUpdate: { ...form }, message: 'Level required!' }));
    } else if (!form?.description || form?.description?.trim() === '') {
      set(() => ({ formUpdate: { ...form }, message: 'Description required!' }));
    } else {
      try {
        set(() => ({ formUpdate: form, loading: true }));
        const { id, ...body } = form;
        const res = await axiosInstance.patch(ApiPath.roles.update(id!), body);
        if (res?.status >= 200 && res?.status < 300) {
          const nextFilter = { ...get().filter, skip: 0, take: get().pageRowAmount };
          set(() => ({ message: null, formUpdate: null, filter: nextFilter, page: 0 }));
          get().roleGetData(true, nextFilter);
          setTimeout(() => get().onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    }
  },
  onDelete: async (id: number) => {
    try {
      set(() => ({ loading: true }));
      const res = await axiosInstance.delete(ApiPath.roles.delete(id));
      if (res?.status >= 200 && res?.status < 300) {
        get().roleGetData(true, get().filter);
      } else {
        set(() => ({ message: res?.statusText }));
      }
    } catch (error) {
      // Deleting happens from the table, where `message` is never rendered — the
      // API rejects roles that still have users, so that reason has to be shown.
      const message = axiosErrorMessage({ error: error as AxiosError });
      set(() => ({ message }));
      CusttomAlertController.getState().handleCustomAlert({
        title: 'ລົບບົດບາດບໍ່ສຳເລັດ',
        message,
        type: CustomAlertType.ERROR,
        icon: CustomAlertIcons.deleteFile,
        negText: 'ປິດ',
        posText: 'ຕົກລົງ',
      });
    } finally {
      setTimeout(() => set(() => ({ loading: false })), 500);
    }
  },
  setMessage: (str: string) => {
    set(() => ({ message: str }));
  },
  openPermissionsModal: (role: RoleInfoModel) => {
    set(() => ({
      selectedRole: role,
      message: null,
      assignedPermissions: {},
      draftPermissionIds: [],
    }));
    get().loadRolePermissions(role.id);
  },
  loadAllPermissions: async () => {
    try {
      set(() => ({ permissionLoading: true }));
      const res = await axiosInstance.get(ApiPath.permissions.all, {
        params: { skip: 0, take: 500 },
      });
      const data: PermissionModel[] = res?.data?.data ?? res?.data ?? [];
      set(() => ({ allPermissions: data }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      set(() => ({ permissionLoading: false }));
    }
  },
  loadRolePermissions: async (roleId: number) => {
    try {
      set(() => ({ permissionLoading: true }));
      const res = await axiosInstance.get(ApiPath.rolePermissions.all, {
        params: { role_id: roleId, skip: 0, take: 500 },
      });
      const rows: RolePermissionModel[] = res?.data?.data ?? res?.data ?? [];
      const assigned: Record<number, string> = {};
      rows.forEach((row) => {
        assigned[row.permission_id] = row.uuid;
      });
      set(() => ({
        assignedPermissions: assigned,
        draftPermissionIds: rows.map((row) => row.permission_id),
      }));
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
    } finally {
      set(() => ({ permissionLoading: false }));
    }
  },
  onToggleDraftPermission: (permissionId: number) => {
    const draft = get().draftPermissionIds;
    set(() => ({
      message: null,
      draftPermissionIds: draft.includes(permissionId)
        ? draft.filter((id) => id !== permissionId)
        : [...draft, permissionId],
    }));
  },
  onToggleDraftGroup: (permissionIds: number[], checked: boolean) => {
    const draft = get().draftPermissionIds;
    set(() => ({
      message: null,
      draftPermissionIds: checked
        ? Array.from(new Set([...draft, ...permissionIds]))
        : draft.filter((id) => !permissionIds.includes(id)),
    }));
  },
  onSaveRolePermissions: async () => {
    const role = get().selectedRole;
    if (!role) return;

    const assigned = get().assignedPermissions;
    const draft = get().draftPermissionIds;
    const added = draft.filter((id) => assigned[id] === undefined);
    const removed = Object.keys(assigned)
      .map(Number)
      .filter((id) => !draft.includes(id));

    if (added.length === 0 && removed.length === 0) {
      get().onHandleModal(null);
      return;
    }

    try {
      set(() => ({ savingPermissions: true, message: null }));
      // bulk-create only inserts, so removals still need one delete each.
      if (added.length > 0) {
        await axiosInstance.post(ApiPath.rolePermissions.bulkCreate, {
          role_id: role.id,
          permission_ids: added,
        });
      }
      for (const permissionId of removed) {
        await axiosInstance.delete(ApiPath.rolePermissions.delete(assigned[permissionId]));
      }
      await get().loadRolePermissions(role.id);
      get().roleGetData(true, get().filter);
      get().onHandleModal(null);
    } catch (error) {
      set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      await get().loadRolePermissions(role.id);
    } finally {
      set(() => ({ savingPermissions: false }));
    }
  },
}));

export default RoleController;
